import { useMutation, useQuery } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import { useState } from "react";
import { api } from "../api/client.js";
import { ErrorBanner, LoadingSkeleton, Panel } from "../components/Common.js";

interface FairTermPoint {
  valuationDate: string;
  fairTermValue: number | null;
  dataGapWarning: string | null;
  fromCache: boolean;
}

interface SavedStructure {
  id: string;
  name: string;
  structureType: string;
  createdAt: string;
  updatedAt: string;
}

export function HistoricalPage() {
  const [structureType, setStructureType] = useState<"accumulator" | "fcn">("accumulator");
  const [ticker, setTicker] = useState("AAPL");
  const [startDate, setStartDate] = useState("2025-01-01");
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [sampling, setSampling] = useState("weekly");

  const savedQuery = useQuery({
    queryKey: ["structures"],
    queryFn: () => api.get<{ structures: SavedStructure[] }>("/structures"),
  });

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ points: FairTermPoint[] }>("/historical-fair-terms", {
        structureType,
        params:
          structureType === "accumulator"
            ? {
                ticker,
                productType: "accumulator",
                valuationDate: endDate,
                tenorMonths: 6,
                guaranteedDays: 20,
                koLevelPct: 1.15,
                leverage: 2,
                baseShares: 1,
                deliveryFrequency: "daily",
                koObservationFrequency: "daily",
                settlementLagDays: 2,
                pricingQuality: "fast",
              }
            : {
                tickers: [ticker],
                valuationDate: endDate,
                tenor: "1Y",
                observationFrequency: "quarterly",
                koLevelPct: 1.0,
                kiLevelPct: 0.7,
                strikeLevelPct: 1.0,
                notional: 100,
                pricingQuality: "fast",
              },
        startDate,
        endDate,
        sampling,
      }),
  });

  const points = mutation.data?.points ?? [];
  const validPoints = points.filter((p) => p.fairTermValue !== null);

  return (
    <div className="space-y-3">
      <Panel title="Historical fair-terms re-striking">
        <p className="mb-3 text-xs text-slate-400">
          For each valuation date, computes the fair strike/coupon a newly-issued structure with these
          same contractual parameters would have required on that date, using only market data available
          as of that date (no look-ahead).
        </p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          <div>
            <label className="field-label">Structure type</label>
            <select className="field-input" value={structureType} onChange={(e) => setStructureType(e.target.value as never)}>
              <option value="accumulator">Accumulator</option>
              <option value="fcn">FCN (single-name proxy)</option>
            </select>
          </div>
          <div>
            <label className="field-label">Ticker</label>
            <input className="field-input" value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())} />
          </div>
          <div>
            <label className="field-label">Start date</label>
            <input type="date" className="field-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="field-label">End date</label>
            <input type="date" className="field-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Sampling</label>
            <select className="field-input" value={sampling} onChange={(e) => setSampling(e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
        <button className="btn-primary mt-3" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? "Computing history…" : "Compute fair-term history"}
        </button>
      </Panel>

      {mutation.error && <ErrorBanner message={(mutation.error as Error).message} />}

      {points.length > 0 && (
        <Panel title={`Fair ${structureType === "fcn" ? "coupon" : "strike"} history (${validPoints.length}/${points.length} points computed)`}>
          <ReactECharts
            option={{
              grid: { left: 50, right: 20, top: 20, bottom: 50 },
              xAxis: { type: "category", data: points.map((p) => p.valuationDate), axisLabel: { color: "#94a3b8", rotate: 45 } },
              yAxis: { type: "value", axisLabel: { color: "#94a3b8", formatter: (v: number) => `${(v * 100).toFixed(0)}%` } },
              tooltip: { trigger: "axis" },
              series: [
                {
                  type: "line",
                  data: points.map((p) => p.fairTermValue),
                  connectNulls: false,
                  itemStyle: { color: "#3b82f6" },
                },
              ],
            }}
            style={{ height: 320 }}
          />
          {points.some((p) => p.dataGapWarning) && (
            <div className="mt-2 text-xs text-amber-300">
              {points.filter((p) => p.dataGapWarning).length} valuation date(s) had a data/model gap and were left blank rather than fabricated.
            </div>
          )}
        </Panel>
      )}

      <Panel title="Saved structures">
        {savedQuery.isLoading && <LoadingSkeleton rows={3} />}
        {savedQuery.data && (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-400">
              <tr>
                <th className="px-2 py-1">Name</th>
                <th className="px-2 py-1">Type</th>
                <th className="px-2 py-1">Updated</th>
              </tr>
            </thead>
            <tbody>
              {savedQuery.data.structures.map((s) => (
                <tr key={s.id} className="border-t border-surface-border/60">
                  <td className="px-2 py-1 font-mono text-xs">{s.name}</td>
                  <td className="px-2 py-1 font-mono text-xs">{s.structureType}</td>
                  <td className="px-2 py-1 font-mono text-xs">{new Date(s.updatedAt).toLocaleString()}</td>
                </tr>
              ))}
              {savedQuery.data.structures.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-2 py-3 text-center text-xs text-slate-500">
                    No saved structures yet. Save one from the Accumulator or FCN pricing screens.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
